// Personal attendance QR codes -- the reverse of the code the school puts on a screen.
//
// TWO MODES, OPPOSITE TRUST MODELS. The screen code (qr_record) is short-lived: it proves you were
// there when it was shown, and the person scanning records their OWN attendance. This one is
// printed on a student card and lives as long as the card does, so it proves nothing about time or
// presence -- it is an IDENTIFIER, not a credential.
//
// Everything about the design follows from that:
//
//   * A personal code can never let its holder record their own attendance. The record is written
//     by an authenticated member of staff operating the scanner, exactly like the paper roll call
//     it replaces: the trust comes from somebody looking at the person.
//   * The secret stops FORGERY -- you cannot print a card carrying a classmate's SID without it.
//     It does not stop lending or photographing a card, and nothing about a static printed code
//     can. That belongs to whoever is holding the scanner, and the platform's job is to give them
//     enough to recognise the person in front of them.
//
// THE CODE CARRIES NO FACTS ABOUT THE PERSON. Only school, who, and the key. No class, no year --
// a card outlives both, and a school that prints once and uses the card for several years should
// not have to reprint because a form changed. Everything that can change, including when the card
// stops working, lives in Firestore, so the school can change it without touching what is printed.

export const QR_VERSION = 2;
export const SUBJECT = { STUDENT: 's', STAFF: 'f' };

/** Where a person's key lives: their SID for a student, their address for a member of staff. */
export const qrDocId = (subjectType, idOrEmail) => (
  subjectType === SUBJECT.STAFF ? String(idOrEmail || '').toLowerCase() : String(idOrEmail || '')
);

/**
 * 20 hex characters from the browser's CSPRNG. Long enough that guessing is not a route in, short
 * enough that the printed code stays coarse -- a dense QR on a laminated card scans badly, and a
 * card that needs three attempts at the door is a card nobody uses.
 */
export const newSecret = () => {
  const bytes = new Uint8Array(10);
  crypto.getRandomValues(bytes);
  return [...bytes].map(b => b.toString(16).padStart(2, '0')).join('');
};

export const buildPayload = ({ schoolId, subjectType, id, secret }) => JSON.stringify({
  v: QR_VERSION, s: schoolId, t: subjectType, i: id, k: secret,
});

export const parsePayload = (text) => {
  let p;
  try { p = JSON.parse(text); } catch { return null; }
  if (!p || !p.s || !p.t || !p.i || !p.k) return null;
  if (p.t !== SUBJECT.STUDENT && p.t !== SUBJECT.STAFF) return null;
  // v1 carried the school year and class. Those cards were never issued outside development, and
  // reading them now would mean honouring an expiry rule that no longer exists.
  if (p.v !== QR_VERSION) return null;
  return p;
};

/**
 * Every reason a scan can fail, each with something the operator can act on. A scanner that says
 * only "invalid" to a queue of two hundred students is a scanner somebody switches off.
 */
export const REJECT = {
  UNREADABLE: 'That is not an attendance card.',
  OTHER_SCHOOL: 'That card belongs to a different school.',
  NOT_ISSUED: 'No card has been issued for that person. Issue one in the Cards tab.',
  REVOKED: 'That card was revoked. A replacement has to be issued and printed.',
  EXPIRED: 'That card has passed its expiry date.',
  BAD_SECRET: 'That card does not match the one on record -- it may be a copy, or it was reissued.',
  ALREADY: 'Already scanned.',
};

/** The dictionary key for each of the above. `reason` stays the English fallback. */
export const rejection = (code) => ({ code, reason: REJECT[code] });

/** Blank means no expiry: a school that prints once and uses the card until it wears out. */
export const isExpired = (expiresOn, todayIso) => !!expiresOn && String(expiresOn) < String(todayIso);

export const cardState = (issued, todayIso) => {
  if (!issued) return 'none';
  if (issued.revokedAt) return 'revoked';
  if (isExpired(issued.expiresOn, todayIso)) return 'expired';
  return 'active';
};

/**
 * Checks a scanned payload against what the school issued. `issued` is the attendance_qr document.
 *
 * The payload's own claims are never trusted -- the same rule the screen-code scanner follows. The
 * card says who; the issued record says whether that still counts.
 */
export const verifyScan = ({ payload, issued, schoolId, todayIso }) => {
  if (!payload) return { ok: false, ...rejection('UNREADABLE') };
  if (payload.s !== schoolId) return { ok: false, ...rejection('OTHER_SCHOOL') };
  if (!issued) return { ok: false, ...rejection('NOT_ISSUED') };
  // Revoked before expired, and both before the secret: a person holding a revoked card should be
  // told it was revoked, not that it "does not match", which reads like the scanner is broken.
  if (issued.revokedAt) return { ok: false, ...rejection('REVOKED') };
  if (isExpired(issued.expiresOn, todayIso)) return { ok: false, ...rejection('EXPIRED') };
  if (!issued.secret || payload.k !== issued.secret) return { ok: false, ...rejection('BAD_SECRET') };
  return { ok: true };
};
